import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import {
    Accordion, Alert, Badge, Box, Button, Checkbox, Divider, Drawer, FileInput, Group, List, Modal, Paper,
    NumberInput, Progress, ScrollArea, Select, SimpleGrid, Skeleton, Stack, Table, Tabs, Text, TextInput,
    Title, Tooltip, ActionIcon,
    CopyButton,
} from '@mantine/core';
import { DateInput } from '@mantine/dates';
import { notifications } from '@mantine/notifications';
import { modals } from '@mantine/modals';
import {
    IconAlertTriangle, IconBell, IconCheck, IconFileText, IconMailForward, IconBrandWhatsapp,
    IconPaperclip, IconPencil, IconPlus, IconReportAnalytics, IconTrash, IconUsers,
    IconCopy, IconExternalLink,
} from '@tabler/icons-react';
import DOMPurify from 'dompurify';
import { Timestamp, addDoc, collection, deleteDoc, doc, getDocs, query, serverTimestamp, updateDoc, where } from 'firebase/firestore';
import { getDownloadURL, ref, uploadBytesResumable } from 'firebase/storage';
import { httpsCallable } from 'firebase/functions';
import { ConfigContext } from '../../contexts';
import { useFirestore, useFunctions, useStorage } from '../../helpers/firebaseContext';
import {
    BaseResult, FundAnnouncement, FundCommunicationAudience, FundCommunicationAudienceRequest,
    FundAnnouncementAttachment, FundCommunicationChannel, FundDistribution, FundLetter, FundReport, FundUpdate,
    Campaign, PublishFundAnnouncementRequest, SendFundLetterRequest,
} from '../../types';
import CampaignDetailsModal from '../campaigns/campaign_details_modal';
import { TextFieldEditor } from '../kyc/components/TextFieldEditorModal';
import { CATALOGUE_COPY, CatalogueLanguage, MANAGER_COPY } from './catalogue_copy';
import {
    CatalogueFund, distributionsNewestFirst, formatAmount, formatDate, formatDateTime, formatNav, formatNumber,
    formatPercent, fundDisplayName, fundSecondaryName, isClosedEnded, latestDistribution,
    distributionNotes, totalDistributedPercent, totalReturnOfCapitalPercent,
} from './catalogue_helpers';

/** Mirrors the 10MB ceiling in storage.rules and the e-mail attachment limit in sendFundLetter. */
const MAX_LETTER_BYTES = 10 * 1024 * 1024;
const ACCEPTED_LETTER_TYPES = [
    'application/pdf',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'image/png',
    'image/jpeg',
];

/**
 * Rendered announcements come from Firestore. The publish callable sanitises before
 * storing, but anything written before that landed — or by any future writer — is still
 * untrusted at render time, so it is sanitised again here.
 */
const SANITIZE_CONFIG = {
    ALLOWED_TAGS: [
        'p', 'br', 'hr', 'div', 'span', 'strong', 'b', 'em', 'i', 'u', 's', 'sub', 'sup',
        'blockquote', 'pre', 'code', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'ul', 'ol', 'li',
        'table', 'thead', 'tbody', 'tr', 'th', 'td', 'caption', 'a', 'img', 'figure', 'figcaption',
    ],
    ALLOWED_ATTR: ['href', 'title', 'target', 'rel', 'src', 'alt', 'width', 'height', 'style', 'colspan', 'rowspan', 'align', 'dir'],
    ALLOWED_URI_REGEXP: /^(?:https?:|mailto:|tel:|data:image\/(?:png|jpe?g|gif|webp);base64,)/i,
    ALLOW_DATA_ATTR: false,
};

/** `DATE` arrives as epoch millis from the website gateway, or a Timestamp when re-written. */
const reportMillis = (report: FundReport): number => {
    const value: any = report.DATE;
    if (!value) return 0;
    if (typeof value === 'number') return value;
    return value?.toMillis?.() || 0;
};

/** formatDate accepts a Date/Timestamp/number; normalise the gateway's DATE for it. */
const reportDate = (report: FundReport): Date | null => {
    const millis = reportMillis(report);
    return millis ? new Date(millis) : null;
};

const formatBytes = (bytes?: number): string => {
    if (!Number.isFinite(bytes) || !bytes) return '';
    if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
    return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
};

interface FundDrawerProps {
    fund: CatalogueFund | null;
    language: CatalogueLanguage;
    /** True only when the signed-in user holds the fund_manager claim AND is assigned to this fund. */
    canManage: boolean;
    canAssignManagers: boolean;
    /** Campaign history contains admin-only audience and delivery data. */
    isAdmin: boolean;
    /**
     * True while the assignment modal is open. The modal is portaled to <body>, i.e.
     * outside this drawer, so without suppressing outside-click/escape the drawer closes
     * the moment the user touches it.
     */
    managerModalOpen?: boolean;
    /** Signed-in user's e-mail, stamped on letters and distributions they add. */
    currentEmail: string;
    /** Distributions from the fund's `dividends` subcollection, loaded by the page. */
    dividends?: FundDistribution[];
    onClose: () => void;
    onAssignManagers: () => void;
}

const FundDrawer = ({
    fund, language, canManage, canAssignManagers, isAdmin, managerModalOpen, currentEmail, dividends,
    onClose, onAssignManagers,
}: FundDrawerProps) => {
    const db = useFirestore();
    const functions = useFunctions();
    const storage = useStorage();
    const config = useContext(ConfigContext);
    const copy = CATALOGUE_COPY[language];

    const [letters, setLetters] = useState<FundLetter[]>([]);
    const [announcements, setAnnouncements] = useState<FundAnnouncement[]>([]);
    const [reports, setReports] = useState<FundReport[]>([]);
    const [campaigns, setCampaigns] = useState<Campaign[]>([]);
    const [campaignsLoading, setCampaignsLoading] = useState(false);
    const [campaignsError, setCampaignsError] = useState<string | null>(null);
    const [campaignDetailsID, setCampaignDetailsID] = useState<string | null>(null);
    /**
     * Locally owned copy of the distributions so an add/delete shows immediately; the
     * page-level hook caches per fund and would otherwise not re-read until remount.
     */
    const [distributions, setDistributions] = useState<FundDistribution[]>([]);
    const [distributionType, setDistributionType] = useState<'pnl' | 'roc'>('pnl');
    const [dividendPercentage, setDividendPercentage] = useState<number | string>('');
    const [dividendDate, setDividendDate] = useState<Date | null>(null);
    const [dividendNotes, setDividendNotes] = useState('');
    const [dividendNotesAr, setDividendNotesAr] = useState('');
    const [editingDistribution, setEditingDistribution] = useState<FundDistribution | null>(null);
    const [editPercentage, setEditPercentage] = useState<number | string>('');
    const [editDate, setEditDate] = useState<Date | null>(null);
    const [editNotes, setEditNotes] = useState('');
    const [editNotesAr, setEditNotesAr] = useState('');
    const [editDistributionType, setEditDistributionType] = useState<'pnl' | 'roc'>('pnl');
    const [loading, setLoading] = useState(false);
    const [loadError, setLoadError] = useState<string | null>(null);
    const [audience, setAudience] = useState<FundCommunicationAudience | null>(null);
    const [busy, setBusy] = useState(false);

    const [letterTitle, setLetterTitle] = useState('');
    const [letterFile, setLetterFile] = useState<File | null>(null);
    const [letterFileError, setLetterFileError] = useState<string | null>(null);
    const [uploadProgress, setUploadProgress] = useState<number | null>(null);

    const [announcementSubject, setAnnouncementSubject] = useState('');
    const [announcementHtml, setAnnouncementHtml] = useState('');
    const [announcementChannels, setAnnouncementChannels] = useState<string[]>(['email', 'whatsapp']);
    /** Uploaded to storage as they are picked, so publishing only passes paths. */
    const [announcementFiles, setAnnouncementFiles] = useState<FundAnnouncementAttachment[]>([]);
    const [fileUploadProgress, setFileUploadProgress] = useState<number | null>(null);

    // The document id, never the payload's `id` field — see CatalogueFund.
    const fundID = fund?.docId || '';
    const announcementTemplateReady = !!config.fundCommunications?.announcementWhatsappTemplateID;
    const letterTemplateReady = !!config.fundCommunications?.letterWhatsappTemplateID;

    const loadCommunications = useCallback(async (targetFundID: string) => {
        setLoading(true);
        setLoadError(null);
        try {
            const [updateSnapshot, reportSnapshot] = await Promise.all([
                // Letters, announcements and distributions share one collection, so this is
                // a single read for all three tabs instead of three separate queries.
                getDocs(collection(db, 'funds', targetFundID, 'updates')),
                // Written by the KFHC website gateway, so ordering happens client side —
                // DATE is sometimes epoch millis and sometimes a Timestamp.
                getDocs(collection(db, 'funds', targetFundID, 'fund_reports_factsheets')),
            ]);

            const updates = updateSnapshot.docs.map((item) => ({ id: item.id, ...item.data() } as FundUpdate));
            const byCreatedAt = (a: FundUpdate, b: FundUpdate) =>
                (b.created_at?.toMillis?.() || 0) - (a.created_at?.toMillis?.() || 0);

            setLetters(updates.filter((item): item is FundLetter => item.type === 'letter').sort(byCreatedAt));
            setAnnouncements(updates.filter((item): item is FundAnnouncement => item.type === 'announcement').sort(byCreatedAt));
            setDistributions(updates.filter((item): item is FundDistribution =>
                item.type === 'dividend' || item.type === 'distribution'));
            setReports(reportSnapshot.docs
                .map((item) => ({ id: item.id, ...item.data() } as FundReport))
                .sort((a, b) => reportMillis(b) - reportMillis(a)));
        } catch (error: any) {
            setLoadError(error?.message || copy.loadFailed);
        } finally {
            setLoading(false);
        }
    }, [db, copy.loadFailed]);

    // Campaigns are visible to admins and to the fund managers assigned to this fund
    // (canManage), matching the firestore.rules read predicate on /campaigns.
    const canSeeCampaigns = isAdmin || canManage;

    const loadCampaigns = useCallback(async (targetFundID: string) => {
        if (!canSeeCampaigns) return;
        setCampaignsLoading(true);
        setCampaignsError(null);
        try {
            // A campaign can target several tags. Filter completed sends client-side to
            // avoid requiring a composite tagsRecipients/status index.
            const snapshot = await getDocs(query(
                collection(db, 'campaigns'),
                where('tagsRecipients', 'array-contains', targetFundID),
            ));
            setCampaigns(snapshot.docs
                .map((item) => ({ id: item.id, ...item.data() } as Campaign))
                .filter((campaign) => campaign.status === 'done')
                .sort((a, b) => (b.run_on?.toMillis?.() || b.created_on?.toMillis?.() || 0)
                    - (a.run_on?.toMillis?.() || a.created_on?.toMillis?.() || 0)));
        } catch (error: any) {
            setCampaignsError(error?.message || copy.loadFailed);
        } finally {
            setCampaignsLoading(false);
        }
    }, [db, canSeeCampaigns, copy.loadFailed]);

    useEffect(() => {
        if (!fundID) return;
        // Reset per-fund state so a previously open fund's drafts never leak across.
        setLetters([]);
        setAnnouncements([]);
        setReports([]);
        setCampaigns([]);
        setCampaignDetailsID(null);
        // Seed from the page's cache so the tab is populated before the read returns.
        setDistributions(dividends || []);
        setDividendPercentage('');
        setDividendDate(null);
        setDividendNotes('');
        setDividendNotesAr('');
        setDistributionType('pnl');
        setEditingDistribution(null);
        setAudience(null);
        setLetterTitle('');
        setLetterFile(null);
        setLetterFileError(null);
        setUploadProgress(null);
        setAnnouncementSubject('');
        setAnnouncementHtml('');
        setAnnouncementFiles([]);
        setFileUploadProgress(null);
        loadCommunications(fundID);
        loadCampaigns(fundID);
        // `dividends` is only a seed; re-running on it would clobber local edits.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fundID, loadCommunications, loadCampaigns]);

    /**
     * Reach preview. Fetched only for managers who can actually send — the counts are
     * client information, and the callable enforces the same restriction server side.
     */
    useEffect(() => {
        if (!fundID || !canManage) return;
        let cancelled = false;
        httpsCallable<FundCommunicationAudienceRequest, BaseResult>(functions, 'getFundCommunicationAudience')({ fundID })
            .then((result) => { if (!cancelled) setAudience(result.data.data as FundCommunicationAudience); })
            .catch(() => { if (!cancelled) setAudience(null); });
        return () => { cancelled = true; };
    }, [functions, fundID, canManage]);

    const orderedDistributions = useMemo(() => distributionsNewestFirst(distributions), [distributions]);
    const totalDistributed = useMemo(() => totalDistributedPercent(distributions), [distributions]);
    const totalReturnOfCapital = useMemo(() => totalReturnOfCapitalPercent(distributions), [distributions]);
    const closedEnded = !!fund && isClosedEnded(fund);
    const latest = latestDistribution(distributions);
    /** Letters that have actually gone out, as opposed to letters merely uploaded. */
    const lettersSent = useMemo(() => letters.filter((letter) => letter.last_sent_at), [letters]);
    const affectedClientsByRm = audience && (
        <Paper withBorder radius="md" p="sm">
            <Group justify="space-between" mb="xs">
                <Text fw={600}>Affected clients by RM</Text>
                <Badge variant="light">{audience.subscribers} clients with qualifying orders</Badge>
            </Group>
            <ScrollArea.Autosize mah={220} type="auto">
                <Table striped highlightOnHover>
                    <Table.Thead><Table.Tr><Table.Th>Relationship Manager</Table.Th><Table.Th ta="right">Affected clients</Table.Th></Table.Tr></Table.Thead>
                    <Table.Tbody>{audience?.affectedClientsByRm?.map((rm) => (
                        <Table.Tr key={rm.email || rm.name}>
                            <Table.Td><Text size="sm" fw={500}>{rm.name}</Text><Text size="xs" c="dimmed">{rm.email || 'No email'}</Text></Table.Td>
                            <Table.Td ta="right"><Badge color={rm.numberOfEffectedClients ? 'blue' : 'gray'} variant="light">{rm.numberOfEffectedClients}</Badge></Table.Td>
                        </Table.Tr>
                    ))}</Table.Tbody>
                </Table>
            </ScrollArea.Autosize>
            {!!audience.unassignedSubscribers && <Alert color="yellow" mt="sm" icon={<IconAlertTriangle size={16} />}>
                {audience.unassignedSubscribers} affected client(s) do not have an RM assigned in rm_email.
            </Alert>}
        </Paper>
    );
    const getMediaID = (pdf?: string) => pdf?.split("MediaArchive:").pop() ?? "";
    const reloadDistributions = async () => {
        const snapshot = await getDocs(query(
            collection(db, 'funds', fundID, 'updates'),
            where('type', 'in', ['dividend', 'distribution']),
        ));
        setDistributions(snapshot.docs.map((item) => ({ id: item.id, ...item.data() } as FundDistribution)));
    };

    const addDistribution = async () => {
        const percentage = Number(dividendPercentage);
        if (!canManage || !dividendDate || !Number.isFinite(percentage)) return;
        setBusy(true);
        try {
            await addDoc(collection(db, 'funds', fundID, 'updates'), {
                type: 'distribution',
                distribution_type: distributionType,
                // Rules require a number and a timestamp: these feed the YTD/MTD calculation.
                percentage,
                date: Timestamp.fromDate(dividendDate),
                ...(dividendNotes.trim() ? { notes: dividendNotes.trim() } : {}),
                ...(dividendNotesAr.trim() ? { notes_ar: dividendNotesAr.trim() } : {}),
                created_at: serverTimestamp(),
                created_by: currentEmail,
            });
            setDividendPercentage('');
            setDividendDate(null);
            setDividendNotes('');
        setDividendNotesAr('');
            await reloadDistributions();
            notifications.show({
                color: 'teal',
                icon: <IconCheck size={16} />,
                title: distributionType === 'roc' ? 'Return of capital added' : 'PNL distribution added',
                message: 'Fund performance figures will refresh once the NAV pipeline reruns.',
            });
        } catch (error: any) {
            notifications.show({
                color: 'red',
                title: 'Could not add distribution',
                message: error?.message || 'The write was rejected.',
                autoClose: false,
            });
        } finally { setBusy(false); }
    };

    const removeDistribution = (dividend: FundDistribution) => {
        modals.openConfirmModal({
            title: MANAGER_COPY.confirmDeleteDistribution,
            centered: true,
            children: (
                <Text size="sm" dir="ltr">
                    Remove the {formatPercent(dividend.percentage, 'en')}{' '}
                    {dividend.type === 'distribution' && dividend.distribution_type === 'roc'
                        ? 'return of capital' : 'PNL distribution'} dated{' '}
                    {formatDate(dividend.date, 'en')}? This also updates the fund's performance figures.
                </Text>
            ),
            labels: { confirm: MANAGER_COPY.delete, cancel: MANAGER_COPY.cancel },
            confirmProps: { color: 'red' },
            onConfirm: async () => {
                setBusy(true);
                try {
                    await deleteDoc(doc(db, 'funds', fundID, 'updates', dividend.id));
                    await reloadDistributions();
                    notifications.show({ color: 'teal', title: 'Distribution removed', message: 'The fund record was updated.' });
                } catch (error: any) {
                    notifications.show({
                        color: 'red', title: 'Could not remove distribution',
                        message: error?.message || 'The delete was rejected.', autoClose: false,
                    });
                } finally { setBusy(false); }
            },
        });
    };

    const beginEditDistribution = (entry: FundDistribution) => {
        setEditingDistribution(entry);
        setEditPercentage(entry.percentage);
        setEditDate(entry.date?.toDate?.() || null);
        setEditNotes(entry.notes || '');
        setEditNotesAr(entry.notes_ar || '');
        setEditDistributionType(entry.type === 'distribution' ? entry.distribution_type : 'pnl');
    };

    const saveDistributionEdit = async () => {
        const percentage = Number(editPercentage);
        if (!editingDistribution || !editDate || !Number.isFinite(percentage)) return;
        setBusy(true);
        try {
            // The type is intentionally immutable; rules enforce the same boundary.
            await updateDoc(doc(db, 'funds', fundID, 'updates', editingDistribution.id), {
                percentage,
                date: Timestamp.fromDate(editDate),
                notes: editNotes.trim(),
                notes_ar: editNotesAr.trim(),
                ...(editingDistribution.type === 'distribution' ? { distribution_type: editDistributionType } : {}),
                updated_at: serverTimestamp(),
                updated_by: currentEmail,
            });
            setEditingDistribution(null);
            await reloadDistributions();
            notifications.show({ color: 'teal', title: 'Entry updated', message: 'Fund performance figures will be recalculated.' });
        } catch (error: any) {
            notifications.show({ color: 'red', title: 'Could not update entry', message: error?.message || 'The write was rejected.', autoClose: false });
        } finally { setBusy(false); }
    };

    /**
     * Mirrors the server's rule so the manager sees the split before sending: videos always
     * become links, and other files are attached only while the 15MB budget lasts.
     */
    const attachmentPlan = useMemo(() => {
        let budget = 15 * 1024 * 1024;
        return announcementFiles.map((file) => {
            const isVideo = (file.content_type || '').startsWith('video/');
            const size = file.size_bytes || 0;
            if (isVideo || size > budget) return { file, delivery: 'linked' as const };
            budget -= size;
            return { file, delivery: 'attached' as const };
        });
    }, [announcementFiles]);

    const uploadAnnouncementFiles = async (files: File[]) => {
        if (!canManage || !files.length) return;
        setBusy(true);
        try {
            const uploaded: FundAnnouncementAttachment[] = [];
            for (let index = 0; index < files.length; index += 1) {
                const file = files[index];
                const safeName = file.name.replace(/[^a-zA-Z0-9._-]/g, '_');
                const storagePath = `funds/${fundID}/announcements/${Date.now()}_${safeName}`;
                const task = uploadBytesResumable(ref(storage, storagePath), file, {
                    contentType: file.type || 'application/octet-stream',
                });
                await new Promise<void>((resolve, reject) => {
                    task.on(
                        'state_changed',
                        (snapshot) => setFileUploadProgress(
                            Math.round(((index + snapshot.bytesTransferred / snapshot.totalBytes) / files.length) * 100),
                        ),
                        reject,
                        () => resolve(),
                    );
                });
                uploaded.push({
                    file_name: file.name,
                    storage_path: storagePath,
                    content_type: file.type || 'application/octet-stream',
                    size_bytes: file.size,
                });
            }
            setAnnouncementFiles((current) => [...current, ...uploaded]);
        } catch (error: any) {
            notifications.show({
                color: 'red',
                title: 'Could not upload file',
                message: error?.message || 'The upload was rejected.',
                autoClose: false,
            });
        } finally {
            setBusy(false);
            setFileUploadProgress(null);
        }
    };

    const pickLetterFile = (file: File | null) => {
        setLetterFile(file);
        if (!file) return setLetterFileError(null);
        if (file.size > MAX_LETTER_BYTES) {
            return setLetterFileError(`This file is ${formatBytes(file.size)}. The limit is 10 MB.`);
        }
        if (file.type && !ACCEPTED_LETTER_TYPES.includes(file.type)) {
            return setLetterFileError('Only PDF, Word and image files can be attached.');
        }
        setLetterFileError(null);
    };

    const uploadLetter = async () => {
        if (!fund || !letterFile || !letterTitle.trim() || !canManage || letterFileError) return;
        setBusy(true);
        setUploadProgress(0);
        try {
            const safeName = letterFile.name.replace(/[^a-zA-Z0-9._-]/g, '_');
            const storagePath = `funds/${fundID}/letters/${Date.now()}_${safeName}`;
            const contentType = letterFile.type || 'application/pdf';
            const task = uploadBytesResumable(ref(storage, storagePath), letterFile, { contentType });

            await new Promise<void>((resolve, reject) => {
                task.on(
                    'state_changed',
                    (snapshot) => setUploadProgress(Math.round((snapshot.bytesTransferred / snapshot.totalBytes) * 100)),
                    reject,
                    () => resolve(),
                );
            });

            await addDoc(collection(db, 'funds', fundID, 'updates'), {
                type: 'letter',
                title: letterTitle.trim(),
                file_name: letterFile.name,
                storage_path: storagePath,
                content_type: contentType,
                size_bytes: letterFile.size,
                created_at: serverTimestamp(),
                created_by: currentEmail,
            });

            setLetterTitle('');
            setLetterFile(null);
            notifications.show({
                color: 'teal',
                icon: <IconCheck size={16} />,
                title: 'Letter attached',
                message: 'The letter is ready to review or send to active RMs.',
            });
            // Re-read rather than optimistically splice: the server stamps created_at/created_by.
            await loadCommunications(fundID);
        } catch (error: any) {
            notifications.show({ color: 'red', title: 'Could not attach letter', message: error?.message || 'Upload failed' });
        } finally {
            setBusy(false);
            setUploadProgress(null);
        }
    };

    const openStorageFile = async (storagePath: string) => {
        try {
            window.open(await getDownloadURL(ref(storage, storagePath)), '_blank', 'noopener,noreferrer');
        } catch (error: any) {
            notifications.show({ color: 'red', title: 'Could not open file', message: error?.message || 'File unavailable' });
        }
    };

    const openLetter = async (letter: FundLetter) => {
        try {
            const url = await getDownloadURL(ref(storage, letter.storage_path));
            window.open(url, '_blank', 'noopener,noreferrer');
        } catch (error: any) {
            notifications.show({ color: 'red', title: 'Could not open letter', message: error?.message || 'File unavailable' });
        }
    };

    /**
     * Fan-out to every RM or holder runs server side for up to nine minutes, so the
     * confirm modal closing is not enough feedback — a pinned notification stays up and
     * is rewritten in place with the delivery counts when the callable returns.
     */
    const runDelivery = async (label: string, send: () => Promise<{ data: BaseResult }>) => {
        const notificationId = `fund-delivery-${Date.now()}`;
        setBusy(true);
        notifications.show({
            id: notificationId,
            loading: true,
            title: `${label}: sending`,
            message: 'Contacting recipients. This can take a few minutes for a large fund.',
            autoClose: false,
            withCloseButton: false,
        });
        try {
            const result = await send();
            notifications.update({
                id: notificationId,
                loading: false,
                color: result.data.status === 'success' ? 'teal' : 'yellow',
                icon: result.data.status === 'success' ? <IconCheck size={16} /> : <IconAlertTriangle size={16} />,
                title: `${label}: ${MANAGER_COPY.deliveryFinished}`,
                message: result.data.message || 'Delivery completed.',
                withCloseButton: true,
                // Partial failures stay on screen until dismissed; they need follow-up.
                autoClose: result.data.status === 'success' ? 8000 : false,
            });
            return result.data;
        } catch (error: any) {
            notifications.update({
                id: notificationId,
                loading: false,
                color: 'red',
                icon: <IconAlertTriangle size={16} />,
                title: `${label}: ${MANAGER_COPY.deliveryFailed}`,
                message: error?.message || 'The request was rejected.',
                withCloseButton: true,
                autoClose: false,
            });
            return null;
        } finally {
            setBusy(false);
        }
    };

    const sendLetter = (letter: FundLetter, channels: FundCommunicationChannel[]) => {
        const reach = channels.includes('email') ? audience?.activeRmsWithEmail : audience?.activeRmsWithMobile;
        modals.openConfirmModal({
            title: MANAGER_COPY.confirmLetterTitle,
            centered: true,
            children: (
                <Stack gap="xs" dir="ltr">
                    <Text size="sm">
                        <strong>{letter.title}</strong> will be sent only to active RMs for{' '}
                        <strong>{fund?.name_en || fundID}</strong> over{' '}
                        <strong>{channels.join(' and ')}</strong>.
                    </Text>
                    {reach == null
                        ? <Text size="sm" c="dimmed">{MANAGER_COPY.audienceUnavailable}.</Text>
                        : <Text size="sm">Recipients: <strong>{reach}</strong> of {audience?.activeRms} active RMs.</Text>}
                    {!!audience?.holdingsWithUnknownUnits && (
                        <Text size="xs" c="orange">
                            {audience.holdingsWithUnknownUnits} holding records have no unit balance and are excluded.
                        </Text>
                    )}
                    {letter.last_sent_at && (
                        <Text size="xs" c="dimmed">
                            Previously sent {formatDateTime(letter.last_sent_at, 'en')} by {letter.last_sent_by}.
                        </Text>
                    )}
                </Stack>
            ),
            labels: { confirm: MANAGER_COPY.confirmSend, cancel: MANAGER_COPY.cancel },
            confirmProps: { color: 'blue', disabled: reach === 0 },
            onConfirm: async () => {
                const outcome = await runDelivery('Letter', () =>
                    httpsCallable<SendFundLetterRequest, BaseResult>(functions, 'sendFundLetter')({
                        fundID, letterID: letter.id, channels,
                    }));
                if (outcome) await loadCommunications(fundID);
            },
        });
    };

    const publishAnnouncement = () => {
        const channels = announcementChannels as FundCommunicationChannel[];
        modals.openConfirmModal({
            title: MANAGER_COPY.confirmAnnouncementTitle,
            centered: true,
            children: (
                <Stack gap="xs" dir="ltr">
                    <Text size="sm">
                        <strong>{announcementSubject.trim()}</strong> will be published for{' '}
                        <strong>{fund?.name_en || fundID}</strong>.
                    </Text>
                    <List size="sm" spacing={4}>
                        {channels.includes('email') && (
                            <List.Item icon={<IconMailForward size={14} />}>
                                Full message emailed to{' '}
                                <strong>{audience?.activeRmsWithEmail ?? '—'}</strong> active RMs
                            </List.Item>
                        )}
                        {channels.includes('whatsapp') && (
                            <List.Item icon={<IconBrandWhatsapp size={14} />}>
                                Short portal notice sent to{' '}
                                <strong>{audience?.activeRmsWithMobile ?? '—'}</strong> RMs on WhatsApp
                            </List.Item>
                        )}
                    </List>
                    <Text size="xs" c="dimmed">This cannot be recalled once sent.</Text>
                </Stack>
            ),
            labels: { confirm: MANAGER_COPY.confirmSend, cancel: MANAGER_COPY.cancel },
            onConfirm: async () => {
                const outcome = await runDelivery('Announcement', () =>
                    httpsCallable<PublishFundAnnouncementRequest, BaseResult>(functions, 'publishFundAnnouncement')({
                        fundID,
                        subject: announcementSubject.trim(),
                        html: announcementHtml,
                        channels,
                        attachments: announcementFiles,
                    }));
                if (outcome) {
                    setAnnouncementSubject('');
                    setAnnouncementHtml('');
                    setAnnouncementFiles([]);
                    await loadCommunications(fundID);
                }
            },
        });
    };

    if (!fund) return null;

    /**
     * Closed-ended (private) funds are not marked to market: NAV, NAV date, YTD and MTD
     * are all blank on them, so listing those rows would be a wall of em-dashes. They get
     * the distribution-and-document view the business asked for instead.
     */
    const facts: [string, string][] = closedEnded
        ? [
            [copy.type, copy.closedEnded],
            [copy.currency, fund.currency || '—'],
            [copy.assetClass, fund.asset_class?.join(', ') || '—'],
            [copy.distributionFrequency, fund.distribution_frequency ? copy[fund.distribution_frequency] : '—'],
            [copy.latestDistribution, latest ? formatPercent(latest.percentage, language) : '—'],
            [copy.latestDistributionDate, latest ? formatDate(latest.date, language) : '—'],
            // Matches the card's stat tile: capital returned, not every distribution.
            [copy.totalReturnOfCapital, formatPercent(totalReturnOfCapital, language)],
            [copy.distributions, formatNumber(orderedDistributions.length, language)],
            [copy.quarterlyReports, formatNumber(reports.length, language)],
            [copy.lettersSent, formatNumber(lettersSent.length, language)],
            [copy.inceptionDate, formatDate(fund.inception_date, language)],
            [copy.returnSinceInception, fund.returns_since_inception || '—'],
            [copy.netAssets, formatAmount(fund.net_assets, language, fund.currency)],
        ]
        : [
            [copy.type, copy.openEnded],
            [copy.currency, fund.currency || '—'],
            [copy.assetClass, fund.asset_class?.join(', ') || '—'],
            [copy.distributionFrequency, fund.distribution_frequency ? copy[fund.distribution_frequency] : '—'],
            [copy.nav, formatNav(fund.nav_per_unit, language)],
            [copy.navDate, formatDate(fund.nav_date, language)],
            [copy.ytd, formatPercent(fund.ytd, language)],
            [copy.mtd, formatPercent(fund.mtd, language)],
            [copy.returnSinceInception, fund.returns_since_inception || formatPercent(fund.itd, language)],
            [copy.inceptionDate, formatDate(fund.inception_date, language)],
            [copy.netAssets, formatAmount(fund.net_assets, language, fund.currency)],
            [copy.minimumUnits, formatNumber(fund.min_units, language)],
            [copy.minimumHolding, formatNumber(fund.minimum_holding, language)],
            [copy.minimumRedemption, formatNumber(fund.minimum_redemption, language)],
            [copy.subscriptionFee, formatPercent(fund.subscription_fees_percent ?? 0, language)],
            [copy.redemptionFee, formatPercent(fund.redemption_fees_percent ?? 0, language)],
            // Dividends matter on open-ended funds too — previously only the count
            // reached the card and the Information tab omitted them entirely.
            [copy.latestDistribution, latest ? formatPercent(latest.percentage, language) : '—'],
            [copy.latestDistributionDate, latest ? formatDate(latest.date, language) : '—'],
            [copy.totalDistributed, formatPercent(totalDistributed, language)],
        ];

    const loadErrorAlert = loadError && (
        <Alert color="red" icon={<IconAlertTriangle size={16} />} title={copy.loadFailed}>
            <Group justify="space-between">
                <Text size="sm">{loadError}</Text>
                <Button size="xs" variant="light" onClick={() => loadCommunications(fundID)}>{copy.retry}</Button>
            </Group>
        </Alert>
    );

    return (
        <Drawer
            opened
            onClose={onClose}
            // The drawer slides in from the reading edge, so it follows the page direction.
            position={language === 'ar' ? 'left' : 'right'}
            size="xl"
            padding="lg"
            closeOnClickOutside={!managerModalOpen}
            closeOnEscape={!managerModalOpen}
            title={<Title order={4}>{fundDisplayName(fund, language)}</Title>}
            scrollAreaComponent={ScrollArea.Autosize}
        >
            <Tabs defaultValue="information" keepMounted={false}>
                <Tabs.List>
                    <Tabs.Tab value="information">{copy.information}</Tabs.Tab>
                    <Tabs.Tab value="dividends">{closedEnded ? copy.distributions : copy.dividends}</Tabs.Tab>
                    <Tabs.Tab value="reports" leftSection={<IconReportAnalytics size={14} />}>
                        {copy.quarterlyReports}
                    </Tabs.Tab>
                    <Tabs.Tab value="letters">{copy.letters}</Tabs.Tab>
                    <Tabs.Tab value="announcements">{copy.announcements}</Tabs.Tab>
                    {canSeeCampaigns && <Tabs.Tab value="campaigns">{copy.campaigns}</Tabs.Tab>}
                </Tabs.List>

                <Tabs.Panel value="information" pt="md">
                    <Stack>
                        <Group justify="space-between" align="flex-start">
                            <Box>
                                <Title order={3}>{fundDisplayName(fund, language)}</Title>
                                <Text c="dimmed">{fundSecondaryName(fund, language)}</Text>
                            </Box>
                            {canAssignManagers && (
                                <Button variant="light" leftSection={<IconUsers size={16} />} onClick={onAssignManagers}>
                                    {MANAGER_COPY.assignManagers}
                                </Button>
                            )}
                        </Group>

                        <SimpleGrid cols={{ base: 1, sm: 2, md: 3 }}>
                            {facts.map(([label, value]) => (
                                <Paper key={label} withBorder p="sm" radius="md">
                                    <Text size="xs" c="dimmed">{label}</Text>
                                    <Text fw={500}>{value}</Text>
                                </Paper>
                            ))}
                        </SimpleGrid>

                        {/* Notes live on the distribution record — there is no fund-level notes
                            field — so the newest distribution's note is the fund's current note. */}
                        {!!distributionNotes(latest, language) && (
                            <Paper withBorder p="sm" radius="md">
                                <Text size="xs" c="dimmed">{copy.notes}</Text>
                                <Text>{distributionNotes(latest, language)}</Text>
                            </Paper>
                        )}

                        <Box>
                            <Text size="sm" fw={600}>{copy.fundManagers}</Text>
                            <Group mt="xs" gap="xs">
                                {fund.fund_manager_emails?.length
                                    ? fund.fund_manager_emails.map((email) => (
                                        <Badge key={email} variant="light" dir="ltr">{email}</Badge>
                                    ))
                                    : <Text c="dimmed" size="sm">{copy.noManagers}</Text>}
                            </Group>
                        </Box>
                    </Stack>
                </Tabs.Panel>

                <Tabs.Panel value="dividends" pt="md">
                    <Stack>
                        {canManage && (
                            /* Fund-manager tooling stays English/LTR by product decision. */
                            <Paper withBorder p="md" radius="md" dir="ltr">
                                <Title order={5}>{MANAGER_COPY.distributionsHeading}</Title>
                                <Text size="sm" c="dimmed" mb="sm">{MANAGER_COPY.distributionsHelp}</Text>
                                <Group align="flex-end" wrap="wrap">
                                    <Select
                                        label={MANAGER_COPY.entryType}
                                        value={distributionType}
                                        onChange={(value) => setDistributionType(value as 'pnl' | 'roc')}
                                        data={[
                                            { value: 'pnl', label: MANAGER_COPY.pnl },
                                            { value: 'roc', label: MANAGER_COPY.returnOfCapital },
                                        ]}
                                        allowDeselect={false}
                                        w={200}
                                    />
                                    <NumberInput
                                        label={MANAGER_COPY.distributionPercentage}
                                        placeholder="Enter %"
                                        value={dividendPercentage}
                                        onChange={setDividendPercentage}
                                        min={0}
                                        decimalScale={4}
                                        rightSection={<Text size="xs">%</Text>}
                                        w={200}
                                    />
                                    <DateInput
                                        label={MANAGER_COPY.distributionDate}
                                        value={dividendDate}
                                        onChange={(value) => setDividendDate(value ? new Date(value) : null)}
                                        firstDayOfWeek={0}
                                        locale="en-gb"
                                        valueFormat="DD/MM/YYYY"
                                        w={200}
                                    />
                                </Group>
                                {/* Notes surface on the catalogue card as its "Important update"
                                    banner, in the RM's chosen language. Arabic is optional and
                                    falls back to English when left blank. */}
                                <Group align="flex-end" gap="sm" wrap="wrap" mt="sm">
                                    <TextInput
                                        label={MANAGER_COPY.distributionNotes}
                                        placeholder={MANAGER_COPY.distributionNotesPlaceholder}
                                        value={dividendNotes}
                                        onChange={(event) => setDividendNotes(event.currentTarget.value)}
                                        style={{ flex: 1, minWidth: 220 }}
                                    />
                                    <TextInput
                                        label={MANAGER_COPY.distributionNotesAr}
                                        placeholder={MANAGER_COPY.distributionNotesArPlaceholder}
                                        value={dividendNotesAr}
                                        onChange={(event) => setDividendNotesAr(event.currentTarget.value)}
                                        dir="rtl"
                                        style={{ flex: 1, minWidth: 220 }}
                                    />
                                </Group>
                                <Button
                                    mt="md"
                                    leftSection={<IconPlus size={16} />}
                                    onClick={addDistribution}
                                    loading={busy}
                                    disabled={busy || !dividendDate || !Number.isFinite(Number(dividendPercentage))}
                                >
                                    {distributionType === 'roc'
                                        ? MANAGER_COPY.addCapitalRedemption
                                        : MANAGER_COPY.addDistribution}
                                </Button>
                            </Paper>
                        )}

                        {orderedDistributions.length ? (
                            <Stack>
                                <Group>
                                    <Badge size="lg" variant="light">
                                        {copy.totalDistributed}: {formatPercent(totalDistributed, language)}
                                    </Badge>
                                    <Badge size="lg" variant="light" color="gray">
                                        {closedEnded ? copy.distributions : copy.dividends}: {formatNumber(orderedDistributions.length, language)}
                                    </Badge>
                                </Group>
                                <Table striped withTableBorder highlightOnHover>
                                    <Table.Thead>
                                        <Table.Tr>
                                            <Table.Th>{copy.type}</Table.Th>
                                            <Table.Th>{copy.date}</Table.Th>
                                            <Table.Th>{copy.percentage}</Table.Th>
                                            <Table.Th>{copy.notes}</Table.Th>
                                            {canManage && <Table.Th aria-label={MANAGER_COPY.delete} />}
                                        </Table.Tr>
                                    </Table.Thead>
                                    <Table.Tbody>
                                        {orderedDistributions.map((dividend, index) => (
                                            <Table.Tr key={dividend.id || index}>
                                                <Table.Td>
                                                    <Badge variant="light" color={dividend.type === 'distribution' && dividend.distribution_type === 'roc' ? 'orange' : 'blue'}>
                                                        {dividend.type === 'distribution' && dividend.distribution_type === 'roc'
                                                            ? copy.returnOfCapital : copy.pnl}
                                                    </Badge>
                                                </Table.Td>
                                                <Table.Td>{formatDate(dividend.date, language)}</Table.Td>
                                                <Table.Td>{formatPercent(dividend.percentage, language)}</Table.Td>
                                                <Table.Td>{distributionNotes(dividend, language) || '—'}</Table.Td>
                                                {canManage && (
                                                    <Table.Td>
                                                        <Group gap={4} wrap="nowrap">
                                                            <ActionIcon variant="subtle" aria-label={MANAGER_COPY.edit} disabled={busy}
                                                                onClick={() => beginEditDistribution(dividend)}>
                                                                <IconPencil size={16} />
                                                            </ActionIcon>
                                                            <ActionIcon variant="subtle" color="red" aria-label={MANAGER_COPY.delete} disabled={busy}
                                                                onClick={() => removeDistribution(dividend)}>
                                                                <IconTrash size={16} />
                                                            </ActionIcon>
                                                        </Group>
                                                    </Table.Td>
                                                )}
                                            </Table.Tr>
                                        ))}
                                    </Table.Tbody>
                                </Table>
                            </Stack>
                        ) : <Text c="dimmed">{copy.noDividends}</Text>}
                    </Stack>
                </Tabs.Panel>

                <Tabs.Panel value="reports" pt="md">
                    <Stack>
                        {loadErrorAlert}
                        {loading && <Skeleton height={72} radius="md" />}
                        {reports.map((report) => (
                            <Paper key={report.id} withBorder p="md" radius="md">
                                <Group justify="space-between" align="center" wrap="wrap">
                                    <Box style={{ flex: 1, minWidth: 200 }}>
                                        <Text fw={600}>{report.TITLE || copy.quarterlyReports}</Text>
                                        <Text size="xs" c="dimmed">{formatDate(reportDate(report), language)}</Text>
                                    </Box>
                                    {report.PDF && (
                                        <Group gap="xs">
                                            <Button
                                                component="a"
                                                href={`https://www.kfhcapital.com.kw/media/${getMediaID(report.PDF)}`}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                size="xs"
                                                variant="light"
                                                leftSection={<IconExternalLink size={14} />}
                                            >
                                                {copy.open}
                                            </Button>
                                            <CopyButton value={`https://www.kfhcapital.com.kw/media/${getMediaID(report.PDF)}`} timeout={2000}>
                                                {({ copied, copy }) => (
                                                    <Tooltip label={copied ? 'Copied' : 'Copy'} withArrow position="right">
                                                        <ActionIcon color={copied ? 'teal' : 'gray'} variant="subtle" onClick={copy}>
                                                            {copied ? <IconCheck size={16} /> : <IconCopy size={16} />}
                                                        </ActionIcon>
                                                    </Tooltip>
                                                )}
                                            </CopyButton>
                                        </Group>
                                    )}
                                </Group>
                            </Paper>
                        ))}
                        {!loading && !reports.length && !loadError && <Text c="dimmed">{copy.noReports}</Text>}
                    </Stack>
                </Tabs.Panel>

                <Tabs.Panel value="letters" pt="md">
                    <Stack>
                        {loadErrorAlert}

                        {canManage && (
                            /* Fund-manager tooling stays English/LTR by product decision. */
                            <Paper withBorder p="md" radius="md" dir="ltr">
                                <Title order={5}>{MANAGER_COPY.lettersHeading}</Title>
                                <Text size="sm" c="dimmed" mb="sm">{MANAGER_COPY.lettersHelp}</Text>
                                {affectedClientsByRm}
                                <TextInput
                                    mt="sm"
                                    label={MANAGER_COPY.letterTitle}
                                    placeholder={MANAGER_COPY.letterTitlePlaceholder}
                                    value={letterTitle}
                                    maxLength={200}
                                    onChange={(event) => setLetterTitle(event.currentTarget.value)}
                                />
                                <FileInput
                                    mt="sm"
                                    label={MANAGER_COPY.file}
                                    description={MANAGER_COPY.fileHelp}
                                    accept={ACCEPTED_LETTER_TYPES.join(',')}
                                    value={letterFile}
                                    onChange={pickLetterFile}
                                    error={letterFileError}
                                    leftSection={<IconFileText size={16} />}
                                />
                                {uploadProgress !== null && (
                                    <Progress mt="sm" value={uploadProgress} striped animated aria-label={MANAGER_COPY.uploading} />
                                )}
                                <Button
                                    mt="md"
                                    onClick={uploadLetter}
                                    loading={busy && uploadProgress !== null}
                                    disabled={busy || !letterTitle.trim() || !letterFile || !!letterFileError}
                                >
                                    {MANAGER_COPY.attach}
                                </Button>
                            </Paper>
                        )}

                        {loading && <Skeleton height={90} radius="md" />}

                        {!!letters.length && (
                            <Group gap="xs">
                                <Badge variant="light">
                                    {copy.lettersSent}: {formatNumber(lettersSent.length, language)}
                                </Badge>
                                <Badge variant="light" color="gray">
                                    {copy.letters}: {formatNumber(letters.length, language)}
                                </Badge>
                            </Group>
                        )}

                        {letters.map((letter) => (
                            <Paper key={letter.id} withBorder p="md" radius="md">
                                <Group justify="space-between" align="flex-start" wrap="wrap">
                                    <Box style={{ flex: 1, minWidth: 220 }}>
                                        <Text fw={600}>{letter.title}</Text>
                                        <Text size="xs" c="dimmed" dir="ltr">
                                            {letter.file_name}
                                            {letter.size_bytes ? ` · ${formatBytes(letter.size_bytes)}` : ''}
                                            {` · ${copy.added} ${formatDate(letter.created_at, language)}`}
                                            {letter.created_by ? ` ${copy.by} ${letter.created_by}` : ''}
                                        </Text>
                                        <Text size="xs" c={letter.last_sent_at ? 'teal.7' : 'dimmed'}>
                                            {letter.last_sent_at
                                                ? `${copy.sentOn}: ${formatDateTime(letter.last_sent_at, language)}`
                                                : copy.neverSent}
                                            {letter.last_send
                                                ? ` · ${letter.last_send.email_recipients} email · ${letter.last_send.whatsapp_recipients} WhatsApp`
                                                : ''}
                                        </Text>
                                    </Box>
                                    <Group gap="xs">
                                        <Button size="xs" variant="default" onClick={() => openLetter(letter)}>{copy.open}</Button>
                                        {canManage && (
                                            <>
                                                <Button
                                                    size="xs"
                                                    leftSection={<IconMailForward size={14} />}
                                                    onClick={() => sendLetter(letter, ['email'])}
                                                    disabled={busy}
                                                >
                                                    {MANAGER_COPY.emailRms}
                                                </Button>
                                                <Tooltip
                                                    label={letterTemplateReady ? MANAGER_COPY.whatsappConfigured : MANAGER_COPY.whatsappNotConfigured}
                                                >
                                                    <Button
                                                        size="xs"
                                                        color="green"
                                                        leftSection={<IconBrandWhatsapp size={14} />}
                                                        onClick={() => sendLetter(letter, ['whatsapp'])}
                                                        disabled={busy || !letterTemplateReady}
                                                    >
                                                        {MANAGER_COPY.whatsappRms}
                                                    </Button>
                                                </Tooltip>
                                            </>
                                        )}
                                    </Group>
                                </Group>
                                {!!letter.last_send?.failed_recipients && (
                                    <Alert mt="sm" color="orange" variant="light" icon={<IconAlertTriangle size={14} />}>
                                        <Text size="xs">
                                            {letter.last_send.failed_recipients} recipient(s) failed on the last send.
                                            {letter.last_send.errors?.[0] ? ` First error: ${letter.last_send.errors[0]}` : ''}
                                        </Text>
                                    </Alert>
                                )}
                            </Paper>
                        ))}

                        {!loading && !letters.length && !loadError && <Text c="dimmed">{copy.noLetters}</Text>}
                    </Stack>
                </Tabs.Panel>

                <Tabs.Panel value="announcements" pt="md">
                    <Stack>
                        {loadErrorAlert}

                        {canManage && (
                            <Paper withBorder p="md" radius="md" dir="ltr">
                                <Title order={5}>{MANAGER_COPY.announcementHeading}</Title>
                                <Text size="sm" c="dimmed">{MANAGER_COPY.announcementHelp}</Text>
                                {audience && (
                                    <Paper withBorder radius="md" p="sm" mt="md">
                                        <Group justify="space-between" mb="xs">
                                            <Text fw={600}>Affected clients by RM</Text>
                                            <Badge variant="light">
                                                {audience.subscribers} clients with qualifying orders
                                            </Badge>
                                        </Group>
                                        <ScrollArea.Autosize mah={220} type="auto">
                                            <Table striped highlightOnHover>
                                                <Table.Thead>
                                                    <Table.Tr>
                                                        <Table.Th>Relationship Manager</Table.Th>
                                                        <Table.Th ta="right">Affected clients</Table.Th>
                                                    </Table.Tr>
                                                </Table.Thead>
                                                <Table.Tbody>
                                                    {(audience.affectedClientsByRm || []).map((rm) => (
                                                        <Table.Tr key={rm.email || rm.name}>
                                                            <Table.Td>
                                                                <Text size="sm" fw={500}>{rm.name}</Text>
                                                                <Text size="xs" c="dimmed">{rm.email || 'No email'}</Text>
                                                            </Table.Td>
                                                            <Table.Td ta="right">
                                                                <Badge color={rm.numberOfEffectedClients > 0 ? 'blue' : 'gray'} variant="light">
                                                                    {rm.numberOfEffectedClients}
                                                                </Badge>
                                                            </Table.Td>
                                                        </Table.Tr>
                                                    ))}
                                                </Table.Tbody>
                                            </Table>
                                        </ScrollArea.Autosize>
                                        {!!audience.unassignedSubscribers && (
                                            <Alert color="yellow" mt="sm" icon={<IconAlertTriangle size={16} />}>
                                                {audience.unassignedSubscribers} affected client(s) do not have an RM assigned in rm_email.
                                            </Alert>
                                        )}
                                    </Paper>
                                )}
                                <TextInput
                                    mt="sm"
                                    label={MANAGER_COPY.emailSubject}
                                    placeholder={MANAGER_COPY.emailSubjectPlaceholder}
                                    value={announcementSubject}
                                    maxLength={200}
                                    onChange={(event) => setAnnouncementSubject(event.currentTarget.value)}
                                />
                                <Box mt="sm">
                                    <TextFieldEditor
                                        label={MANAGER_COPY.emailMessage}
                                        value={announcementHtml}
                                        onChange={setAnnouncementHtml}
                                        onRemove={() => setAnnouncementHtml('')}
                                        placeholder={MANAGER_COPY.emailPlaceholder}
                                        language="en"
                                    />
                                </Box>
                                <Box mt="md">
                                    <Text size="sm" fw={500}>{MANAGER_COPY.announcementFiles}</Text>
                                    <Text size="xs" c="dimmed" mb="xs">{MANAGER_COPY.announcementFilesHelp}</Text>
                                    <FileInput
                                        multiple
                                        clearable
                                        value={[]}
                                        placeholder={MANAGER_COPY.addFiles}
                                        leftSection={<IconPaperclip size={16} />}
                                        disabled={busy}
                                        onChange={(files) => uploadAnnouncementFiles(files as File[])}
                                    />
                                    {fileUploadProgress !== null && (
                                        <Progress mt="xs" value={fileUploadProgress} striped animated
                                            aria-label={MANAGER_COPY.uploadingFiles} />
                                    )}
                                    {attachmentPlan.map(({ file, delivery }) => (
                                        <Group key={file.storage_path} justify="space-between" mt="xs" wrap="nowrap">
                                            <Group gap="xs" wrap="nowrap" style={{ minWidth: 0 }}>
                                                <Text size="sm" truncate>{file.file_name}</Text>
                                                <Text size="xs" c="dimmed">{formatBytes(file.size_bytes)}</Text>
                                                <Badge size="sm" variant="light" color={delivery === 'attached' ? 'blue' : 'orange'}>
                                                    {delivery === 'attached' ? MANAGER_COPY.willAttach : MANAGER_COPY.willLink}
                                                </Badge>
                                            </Group>
                                            <ActionIcon
                                                variant="subtle"
                                                color="red"
                                                aria-label={MANAGER_COPY.removeFile}
                                                disabled={busy}
                                                onClick={() => setAnnouncementFiles((current) =>
                                                    current.filter((item) => item.storage_path !== file.storage_path))}
                                            >
                                                <IconTrash size={16} />
                                            </ActionIcon>
                                        </Group>
                                    ))}
                                </Box>

                                <Checkbox.Group
                                    mt="sm"
                                    label={MANAGER_COPY.notifyUsing}
                                    value={announcementChannels}
                                    onChange={setAnnouncementChannels}
                                >
                                    <Group mt="xs">
                                        <Checkbox
                                            value="email"
                                            label={`${MANAGER_COPY.emailFull}${audience ? ` — ${audience.activeRmsWithEmail} RMs` : ''}`}
                                        />
                                        <Tooltip label={announcementTemplateReady ? MANAGER_COPY.whatsappConfigured : MANAGER_COPY.whatsappNotConfigured}>
                                            <Checkbox
                                                value="whatsapp"
                                                disabled={!announcementTemplateReady}
                                                label={`${MANAGER_COPY.whatsappShort}${audience ? ` — ${audience.activeRmsWithMobile} RMs` : ''}`}
                                            />
                                        </Tooltip>
                                    </Group>
                                </Checkbox.Group>
                                <Button
                                    mt="md"
                                    leftSection={<IconBell size={16} />}
                                    onClick={publishAnnouncement}
                                    loading={busy}
                                    disabled={busy || !announcementSubject.trim() || !announcementHtml.trim() || !announcementChannels.length}
                                >
                                    {MANAGER_COPY.publish}
                                </Button>
                            </Paper>
                        )}

                        {loading && <Skeleton height={90} radius="md" />}

                        {/* One Accordion holding every item, so opening one closes the last. */}
                        {!!announcements.length && (
                            <Accordion variant="contained">
                                {announcements.map((announcement) => (
                                    <Accordion.Item key={announcement.id} value={announcement.id}>
                                        <Accordion.Control>
                                            <Group justify="space-between" wrap="nowrap" pr="sm">
                                                <Text fw={600} lineClamp={1}>{announcement.subject}</Text>
                                                <Group gap="xs" wrap="nowrap">
                                                    {announcement.status === 'partial' && <Badge size="sm" color="orange">Partial</Badge>}
                                                    {announcement.status === 'failed' && <Badge size="sm" color="red">Failed</Badge>}
                                                    <Text size="xs" c="dimmed">{formatDate(announcement.created_at, language)}</Text>
                                                </Group>
                                            </Group>
                                        </Accordion.Control>
                                        <Accordion.Panel>
                                            <Box
                                                className="fund-announcement-body"
                                                dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(announcement.html || '', SANITIZE_CONFIG) }}
                                            />
                                            {!!announcement.attachments?.length && (
                                                <Box mt="sm">
                                                    <Text size="xs" fw={600}>{MANAGER_COPY.attachments}</Text>
                                                    {announcement.attachments.map((attachment) => (
                                                        <Group key={attachment.storage_path} gap="xs" mt={4} wrap="nowrap">
                                                            <IconPaperclip size={14} />
                                                            <Text
                                                                size="xs"
                                                                style={{ cursor: 'pointer', textDecoration: 'underline' }}
                                                                onClick={() => openStorageFile(attachment.storage_path)}
                                                            >
                                                                {attachment.file_name}
                                                            </Text>
                                                            <Text size="xs" c="dimmed">{formatBytes(attachment.size_bytes)}</Text>
                                                            {attachment.delivery === 'linked' && (
                                                                <Badge size="xs" variant="light" color="orange">
                                                                    {MANAGER_COPY.willLink}
                                                                </Badge>
                                                            )}
                                                        </Group>
                                                    ))}
                                                </Box>
                                            )}
                                            <Divider my="sm" />
                                            <Text size="xs" c="dimmed" dir="ltr">
                                                {copy.by} {announcement.created_by}
                                                {` · ${announcement.email_recipients || 0} email`}
                                                {` · ${announcement.whatsapp_recipients || 0} WhatsApp`}
                                                {announcement.failed_recipients ? ` · ${announcement.failed_recipients} failed` : ''}
                                            </Text>
                                        </Accordion.Panel>
                                    </Accordion.Item>
                                ))}
                            </Accordion>
                        )}

                        {!loading && !announcements.length && !loadError && <Text c="dimmed">{copy.noAnnouncements}</Text>}
                    </Stack>
                </Tabs.Panel>

                {canSeeCampaigns && (
                    <Tabs.Panel value="campaigns" pt="md">
                        <Stack>
                            {campaignsError && <Alert color="red" icon={<IconAlertTriangle size={16} />}>{campaignsError}</Alert>}
                            {campaignsLoading && <Skeleton height={90} radius="md" />}
                            {campaigns.map((campaign) => (
                                <Paper key={campaign.id} withBorder p="md" radius="md">
                                    <Group justify="space-between" align="center" wrap="wrap">
                                        <Box>
                                            <Text fw={600}>{campaign.title || copy.campaign}</Text>
                                            <Text size="xs" c="dimmed">
                                                {copy.sentOn}: {formatDateTime(campaign.run_on || campaign.created_on, language)}
                                            </Text>
                                            <Group gap="xs" mt="xs">
                                                {campaign.type?.map((type) => <Badge key={type} size="sm" variant="light">{type}</Badge>)}
                                            </Group>
                                        </Box>
                                        <Button size="xs" variant="default" onClick={() => setCampaignDetailsID(campaign.id)}>
                                            {copy.viewCampaign}
                                        </Button>
                                    </Group>
                                </Paper>
                            ))}
                            {!campaignsLoading && !campaigns.length && !campaignsError && <Text c="dimmed">{copy.noCampaigns}</Text>}
                        </Stack>
                    </Tabs.Panel>
                )}
            </Tabs>
            <Modal opened={!!editingDistribution} onClose={() => setEditingDistribution(null)}
                title={MANAGER_COPY.editEntry} centered dir="ltr">
                <Stack>
                    <Text size="sm" c="dimmed">
                        {editingDistribution?.type === 'distribution' && editingDistribution.distribution_type === 'roc'
                            ? MANAGER_COPY.returnOfCapital : MANAGER_COPY.pnl}
                    </Text>
                    {editingDistribution?.type === 'distribution' && (
                        <Select label={MANAGER_COPY.entryType} value={editDistributionType}
                            onChange={(value) => setEditDistributionType(value as 'pnl' | 'roc')}
                            data={[{ value: 'pnl', label: MANAGER_COPY.pnl }, { value: 'roc', label: MANAGER_COPY.returnOfCapital }]}
                            allowDeselect={false} />
                    )}
                    <NumberInput label={MANAGER_COPY.distributionPercentage} value={editPercentage}
                        onChange={setEditPercentage} min={0} decimalScale={4} rightSection={<Text size="xs">%</Text>} />
                    <DateInput label={MANAGER_COPY.distributionDate} value={editDate}
                        onChange={(value) => setEditDate(value ? new Date(value) : null)} firstDayOfWeek={0}
                        locale="en-gb" valueFormat="DD/MM/YYYY" />
                    <TextInput label={MANAGER_COPY.distributionNotes} value={editNotes}
                        onChange={(event) => setEditNotes(event.currentTarget.value)} />
                    <TextInput label={MANAGER_COPY.distributionNotesAr} value={editNotesAr} dir="rtl"
                        onChange={(event) => setEditNotesAr(event.currentTarget.value)} />
                    <Group justify="flex-end">
                        <Button variant="default" onClick={() => setEditingDistribution(null)}>{MANAGER_COPY.cancel}</Button>
                        <Button onClick={saveDistributionEdit} loading={busy}
                            disabled={!editDate || !Number.isFinite(Number(editPercentage))}>{MANAGER_COPY.save}</Button>
                    </Group>
                </Stack>
            </Modal>
            {canSeeCampaigns && <CampaignDetailsModal campaignId={campaignDetailsID} onClose={() => setCampaignDetailsID(null)} />}
        </Drawer >
    );
};

export default FundDrawer;
